import React, { useCallback, useRef, useState } from 'react';
import { Upload, X, ImageIcon, Music } from 'lucide-react';

export default function DropZone({
  accept,
  label,
  hint,
  onFile,
  previewSrc,
  fileName,
  onClear,
  error,
  isAudio = false,
  compact = false,
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback((files) => {
    if (files && files[0]) onFile(files[0]);
  }, [onFile]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const hasContent = previewSrc || fileName;

  return (
    <div>
      {label && <label className="block text-xs font-medium text-neutral-600 mb-1.5">{label}</label>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative rounded-lg border-2 border-dashed transition-colors cursor-pointer flex items-center gap-3 ${compact ? 'p-2.5' : 'p-4'} ${
          dragging ? 'border-orange-400 bg-orange-50' : 'border-neutral-200 hover:border-neutral-300 bg-neutral-50'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        {hasContent ? (
          <>
            {previewSrc ? (
              <img src={previewSrc} alt="preview" className="w-10 h-10 rounded object-cover border border-neutral-200 flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded bg-orange-50 border border-orange-200 flex items-center justify-center flex-shrink-0">
                <Music className="w-4 h-4 text-orange-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-neutral-800 truncate font-medium">{fileName}</p>
              <p className="text-xs text-neutral-400">Click or drop to replace</p>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="p-1.5 rounded-full hover:bg-neutral-200 text-neutral-500 flex-shrink-0"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <div className="w-9 h-9 rounded-full bg-white border border-neutral-200 flex items-center justify-center flex-shrink-0">
              {isAudio ? <Music className="w-4 h-4 text-neutral-500" /> : <ImageIcon className="w-4 h-4 text-neutral-500" />}
            </div>
            <div className="flex-1">
              <p className="text-sm text-neutral-700 font-medium flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5" /> Drop or click to upload
              </p>
              {hint && <p className="text-xs text-neutral-400 mt-0.5">{hint}</p>}
            </div>
          </>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
    </div>
  );
}
